/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 16.06.17.
 */

import _ from "underscore";
import {vsprintf} from "sprintf-js";

/**
 * Provides together data and schema for this data.
 * Value selectors can be in form:
 *      selector ::= leg | selector + '/' + leg
 *      leg ::= fieldName  | '[' + recordNo + ']' | recordNo
 *      fieldName - {string} object field name
 *      recordNo - {integer} array record number
 *
 */
class DataProvider {
    /**
     *
     * @param {Schema} schema
     * @param {Array|Object} data
     */
    constructor(schema, data) {
        /**
         * @type {Schema}
         */
        this.schema = schema;
        /**
         * @type {*}
         */
        this.data = data;
        this.providers = {};
        this.formatters = {};
    }

    /**
     * Binds new data to the same schema
     * @param {*} data
     */
    bindData(data) {
        this.data = data;
    }

    /**
     * Returns data
     * @returns {*}
     */
    getData() {
        return this.data;
    }

    getRowCount() {
        if (_.isArray(this.data)) return this.data.length;
        return 0;
    }

    /**
     * Returns schema
     * @returns {Schema}
     */
    getSchema() {
        return this.schema;
    }

    /**
     * Returns record schema (in case when data is an array)
     * @returns {Schema}
     */
    getRecordSchema() {
        return this.schema.field("*");
    }

    /**
     * Returns current data type
     * @returns {('object'|'array'|'string'|'number'|'integer'|'boolean')} Field type
     */
    getType() {
        return this.schema.getType();
    }

    /**
     * Returns true if data is object
     * @returns {boolean}
     */
    isObject() {
        return this.schema.getType() === "object";
    }

    /**
     * Returns true if data is array
     * @returns {boolean}
     */
    isArray() {
        return this.schema.getType() === "array";
    }

    /**
     * Returns true if data is simple value
     * @returns {boolean}
     */
    isSimpleValue() {
        switch (this.schema.getType()) {
            case "object":
            case "array":
                return false;
            default:
                return true;
        }
    }

    /**
     *
     * @param {number|string|string[]} field
     * @returns {DataProvider}
     */
    getProvider(field) {
        let fieldName = field;
        if (_.isArray(field)) fieldName = _.reduce(field, function (memo, val) {
            return memo.toString() + "/" + val.toString();
        });
        if (this.providers[fieldName]) return this.providers[fieldName];
        let dataProvider = new DataProvider(this.schema.field(field), this.getValue(field));
        this.providers[fieldName] = dataProvider;
        return dataProvider;
    }

    /**
     * Returns value of specified field.
     * @param {string|number|string[]} field Simple field name, record number, hierarchical field name or hierarchical field names array
     * @returns {*}
     */
    getValue(field) {
        if (field == null || field === "") return this.data;

        let fieldParts = [];
        if (_.isNumber(field)) fieldParts = [field];
        else if (_.isArray(field)) fieldParts = field;
        else fieldParts = field.split("/");

        let currentData = this.data;
        for (let f of fieldParts) {
            if (_.isArray(currentData)) {
                // Array
                let idx = null;
                if (_.isNumber(f)) idx = f;
                else if (f.length > 2 && f.charAt(0) === "[" && f.charAt(f.length - 1) === "]") idx = Number.parseInt(f.slice(1, f.length - 1), 10);
                else idx = Number.parseInt(f, 10);

                if (isNaN(idx) || currentData.length <= idx) return null;
                currentData = currentData[idx];
            } else {
                // Object
                if (!_.has(currentData, f)) return null;
                currentData = currentData[f];
            }
        }
        return currentData;
    }

    /**
     * Set value of the specified field.
     * @param {string|number|string[]} field Simple field name, record number, hierarchical field name or hierarchical field names array
     * @param {Object} value
     * @returns {*}
     */
    setValue(field, value) {

    }

    /**
     * Add value to the value list of the specified field. Field must be array.
     * @param field
     * @param value
     */
    addValue(field, value) {

    }

    removeValue(field, value) {

    }

    /**
     * Returns field title based by UI Schema "ui:titleField" element value. If this element is missing
     * return field title based by "title" element value.
     * @returns {String}
     */
    getTitle() {
        let schema = this.getSchema();
        if (!schema) return null;
        let titleField = schema.getSchemaElement("titleField");
        if (!titleField) {
            return schema.getTitle();
        }
        if (!_.isArray(titleField)) return this.getValue(titleField);
        let params = _.map(titleField.slice(1), function (field) {
            return this.getValue(field);
        }.bind(this));
        return vsprintf(titleField[0], params);
    }
}

export default DataProvider;
